<template>
  <div class="landing" ref="landing">
    <!-- Custom Cursor -->
    <div class="cursor" ref="cursor"></div>
    <div class="cursor-follower" ref="cursorFollower"></div>

    <!-- Navigation -->
    <nav class="nav" :class="{ 'nav--scrolled': scrolled }">
      <div class="nav__logo">
        <span class="nav__logo-badge">B</span>
        <span class="nav__logo-text">ubba</span>
      </div>
      <div class="nav__links">
        <a href="#services" class="nav__link">Services</a>
        <a href="#partners" class="nav__link">Partners</a>
        <a href="#about" class="nav__link">About</a>
      </div>
      <a href="#join" class="nav__cta">Get Started</a>
    </nav>

    <!-- Hero Section -->
    <section class="hero" ref="heroSection">
      <div class="hero__bg-text" aria-hidden="true">BUBBA</div>

      <div class="hero__content">
        <div class="hero__eyebrow" :class="{ visible: revealed }">
          <span class="hero__eyebrow-line"></span>
          <span>Pet Services, Elevated</span>
        </div>

        <h1 class="hero__title">
          <span class="hero__title-line" :class="{ visible: revealed }" style="--delay: 0.1s">
            <em>Where</em> pets
          </span>
          <span class="hero__title-line hero__title-line--indent" :class="{ visible: revealed }" style="--delay: 0.2s">
            &amp; care
          </span>
          <span class="hero__title-line hero__title-line--accent" :class="{ visible: revealed }" style="--delay: 0.3s">
            converge.
          </span>
        </h1>

        <p class="hero__sub" :class="{ visible: revealed }" style="--delay: 0.5s">
          Connect with trusted groomers, vets, daycares, and more —<br>
          curated for the modern pet family.
        </p>

        <div class="hero__actions" :class="{ visible: revealed }" style="--delay: 0.65s">
          <a href="#join" class="btn btn--primary" @mousemove="magnetBtn" @mouseleave="resetBtn">
            <span>Find Services</span>
            <svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </a>
          <a href="#partners" class="btn btn--ghost">Become a Partner</a>
        </div>
      </div>

      <!-- Hero Imagery Mosaic -->
      <div class="hero__mosaic" :class="{ visible: revealed }">
        <div class="mosaic__card mosaic__card--1" @mousemove="tiltCard" @mouseleave="resetCard">
          <div class="mosaic__img-wrap">
            <div class="mosaic__img mosaic__img--dog"></div>
          </div>
          <div class="mosaic__label">Grooming</div>
        </div>
        <div class="mosaic__card mosaic__card--2" @mousemove="tiltCard" @mouseleave="resetCard">
          <div class="mosaic__img-wrap">
            <div class="mosaic__img mosaic__img--vet"></div>
          </div>
          <div class="mosaic__label">Veterinary</div>
        </div>
        <div class="mosaic__card mosaic__card--3" @mousemove="tiltCard" @mouseleave="resetCard">
          <div class="mosaic__img-wrap">
            <div class="mosaic__img mosaic__img--daycare"></div>
          </div>
          <div class="mosaic__label">Daycare</div>
        </div>
        <div class="mosaic__stat">
          <span class="mosaic__stat-num">2,400+</span>
          <span class="mosaic__stat-label">Trusted Partners</span>
        </div>
      </div>

      <!-- Scroll indicator -->
      <div class="hero__scroll" :class="{ visible: revealed }" style="--delay: 1s">
        <span>Scroll</span>
        <div class="hero__scroll-line"></div>
      </div>
    </section>

    <!-- Marquee Strip -->
    <div class="marquee-strip">
      <div class="marquee-track">
        <span v-for="i in 3" :key="i">
          <em>Grooming</em> · Veterinary · <em>Daycare</em> · Training · <em>Boarding</em> · Nutrition · <em>Spa</em> · Emergency Care ·&nbsp;
        </span>
      </div>
    </div>

    <!-- Services Section -->
    <section class="services" id="services">
      <div class="services__header">
        <div>
          <div class="section-label">
            <span class="section-label__line"></span>What We Offer
          </div>
          <h2 class="services__title">
            Everything your<br><em>pet deserves</em>
          </h2>
        </div>
      </div>

      <div class="services__grid">
        <div
          v-for="(service, i) in services"
          :key="service.name"
          class="service-card"
          :style="`--i: ${i}`"
        >
          <div class="service-card__number">0{{ i + 1 }}</div>
          <div class="service-card__icon" v-html="service.icon"></div>
          <h3 class="service-card__name">{{ service.name }}</h3>
          <p class="service-card__desc">{{ service.desc }}</p>
          <div class="service-card__cta">
            <span>Explore</span>
            <svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </div>
          <div class="service-card__bg" :style="`background: ${service.color}`"></div>
        </div>
      </div>
    </section>

    <!-- Editorial Feature -->
    <section class="feature" id="about">
      <div class="feature__left">
        <div class="feature__img-stack">
          <div class="feature__img feature__img--back"></div>
          <div class="feature__img feature__img--front">
            <div class="feature__img-tag">Est. 2024</div>
          </div>
        </div>
      </div>
      <div class="feature__right">
        <div class="section-label">
          <span class="section-label__line"></span>Our Story
        </div>
        <h2 class="feature__title">
          Built for the<br>bond between<br><em>you &amp; your pet</em>
        </h2>
        <p class="feature__body">
          Bubba started with a simple belief: every pet deserves exceptional care, and every owner deserves peace of mind. We've built a platform that connects you with vetted, passionate professionals who treat your furry family like their own.
        </p>
        <div class="feature__stats">
          <div class="feature__stat" v-for="(stat, i) in featureStats" :key="stat.label" :style="`--i: ${i}`">
            <span class="feature__stat-num">{{ stat.num }}</span>
            <span class="feature__stat-label">{{ stat.label }}</span>
          </div>
        </div>
        <a href="#join" class="btn btn--primary">Learn More</a>
      </div>
    </section>

    <!-- Partners Section -->
    <section class="partners" id="partners">
      <div class="partners__inner">
        <div class="section-label section-label--light">
          <span class="section-label__line section-label__line--light"></span>Join Our Network
        </div>
        <h2 class="partners__title">
          Grow your<br>practice with <em>Bubba</em>
        </h2>
        <p class="partners__body">
          Whether you're a solo groomer or a full veterinary clinic, Bubba's partner console gives you the tools to manage bookings, clients, and growth — all in one place.
        </p>

        <div class="partner-cards">
          <div class="partner-card" v-for="(perk, i) in partnerPerks" :key="perk.title" :style="`--i: ${i}`">
            <div class="partner-card__icon" v-html="perk.icon"></div>
            <h3 class="partner-card__title">{{ perk.title }}</h3>
            <p class="partner-card__desc">{{ perk.desc }}</p>
          </div>
        </div>

        <a href="#join" class="btn btn--terracotta">Partner With Us</a>
      </div>

      <div class="partners__diagonal" aria-hidden="true"></div>
    </section>

    <!-- Testimonials -->
    <section class="testimonials">
      <div class="testimonials__header">
        <div class="section-label">
          <span class="section-label__line"></span>Voices
        </div>
        <h2 class="testimonials__title">Loved by <em>pet families</em></h2>
      </div>
      <div class="testimonials__track">
        <div
          class="testimonial"
          v-for="(t, i) in testimonials"
          :key="i"
          :class="{ 'testimonial--active': activeTestimonial === i }"
          @click="activeTestimonial = i"
        >
          <div class="testimonial__quote">"</div>
          <p class="testimonial__text">{{ t.text }}</p>
          <div class="testimonial__author">
            <div class="testimonial__avatar" :style="`background: ${t.color}`">{{ t.initials }}</div>
            <div>
              <div class="testimonial__name">{{ t.name }}</div>
              <div class="testimonial__pet">{{ t.pet }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section" id="join">
      <div class="cta-section__bg-word" aria-hidden="true">CARE</div>
      <div class="cta-section__content">
        <h2 class="cta-section__title">
          Ready to give your<br>pet the <em>best</em>?
        </h2>
        <p class="cta-section__sub">Join thousands of pet families who've discovered a better way to care.</p>
        <div class="cta-section__actions">
          <a href="#" class="btn btn--primary btn--large">
            <span>Find Services Near You</span>
            <svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </a>
          <a href="#" class="btn btn--outline btn--large">Download the App</a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer__top">
        <div class="footer__brand">
          <div class="footer__logo">
            <span class="footer__logo-badge">B</span>
            <span class="footer__logo-text">ubba</span>
          </div>
          <p class="footer__tagline">Pet services, elevated.</p>
        </div>
        <div class="footer__nav">
          <div class="footer__nav-col">
            <span class="footer__nav-label">Platform</span>
            <a href="#">For Pet Owners</a>
            <a href="#">For Partners</a>
            <a href="#">Mobile App</a>
            <a href="#">Console</a>
          </div>
          <div class="footer__nav-col">
            <span class="footer__nav-label">Services</span>
            <a href="#">Grooming</a>
            <a href="#">Veterinary</a>
            <a href="#">Daycare</a>
            <a href="#">Training</a>
          </div>
          <div class="footer__nav-col">
            <span class="footer__nav-label">Company</span>
            <a href="#">About Us</a>
            <a href="#">Blog</a>
            <a href="#">Careers</a>
            <a href="#">Contact</a>
          </div>
        </div>
      </div>
      <div class="footer__bottom">
        <span>© 2024 Bubba. All rights reserved.</span>
        <div class="footer__legal">
          <a href="#">Privacy</a>
          <a href="#">Terms</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { definePageMeta } from '#imports'
import { ref, onMounted, onUnmounted } from 'vue'

definePageMeta({ layout: false })

const cursor = ref(null)
const cursorFollower = ref(null)
const revealed = ref(false)
const scrolled = ref(false)
const activeTestimonial = ref(0)

const services = [
  {
    name: 'Grooming',
    desc: 'Professional grooming services tailored to every breed — from bath & brush to full spa treatments.',
    icon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 32c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="20" cy="18" r="3" stroke="currentColor" stroke-width="2"/><circle cx="28" cy="18" r="3" stroke="currentColor" stroke-width="2"/><path d="M14 26c-2-1-4-3-4-6 0-4 3-7 6-7M34 26c2-1 4-3 4-6 0-4-3-7-6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
    color: 'oklch(0.63 0.15 50 / 0.08)'
  },
  {
    name: 'Veterinary',
    desc: 'Connect with licensed vets for routine check-ups, urgent care, and specialist consultations.',
    icon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="10" y="18" width="28" height="20" rx="4" stroke="currentColor" stroke-width="2"/><path d="M18 10h12M24 10v8M20 28h8M24 24v8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
    color: 'oklch(0.4 0.08 152 / 0.08)'
  },
  {
    name: 'Daycare',
    desc: 'Safe, supervised play and socialization while you\'re at work — your pet will never miss you.',
    icon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 36V20l12-8 12 8v16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><rect x="19" y="26" width="10" height="10" rx="1" stroke="currentColor" stroke-width="2"/></svg>`,
    color: 'oklch(0.74 0.11 77 / 0.1)'
  },
  {
    name: 'Training',
    desc: 'Expert trainers using positive reinforcement techniques to bring out the best in your companion.',
    icon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 34l6-10 4 6 4-8 6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="24" cy="16" r="5" stroke="currentColor" stroke-width="2"/></svg>`,
    color: 'oklch(0.56 0.13 33 / 0.08)'
  },
  {
    name: 'Boarding',
    desc: 'Home-away-from-home stays with verified hosts who love pets as much as you do.',
    icon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 28h28M10 28v8h28v-8M10 28l4-10h20l4 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 36v-5h8v5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
    color: 'oklch(0.52 0.1 306 / 0.08)'
  },
  {
    name: 'Nutrition',
    desc: 'Tailored meal plans and expert dietary advice from certified pet nutritionists.',
    icon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 20c0-4.4 3.6-8 8-8s8 3.6 8 8v2H16v-2z" stroke="currentColor" stroke-width="2"/><path d="M14 22h20v4a8 8 0 01-16 0v-4z" stroke="currentColor" stroke-width="2"/><path d="M24 36v4M20 40h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
    color: 'oklch(0.58 0.1 159 / 0.08)'
  },
]

const featureStats = [
  { num: '98%', label: 'Satisfaction Rate' },
  { num: '50k+', label: 'Happy Pets Served' },
  { num: '120+', label: 'Cities' },
]

const partnerPerks = [
  {
    title: 'Smart Scheduling',
    desc: 'Intelligent booking system that eliminates double-bookings and fills your calendar automatically.',
    icon: `<svg viewBox="0 0 32 32" fill="none"><rect x="4" y="6" width="24" height="22" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M4 12h24M10 4v4M22 4v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M9 18h4v4H9z" fill="currentColor" opacity=".3"/></svg>`
  },
  {
    title: 'Client Management',
    desc: 'Full pet profiles, medical history, and communication history — all in your partner console.',
    icon: `<svg viewBox="0 0 32 32" fill="none"><circle cx="16" cy="11" r="5" stroke="currentColor" stroke-width="1.5"/><path d="M6 27c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M20 15l2 2 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
  },
  {
    title: 'Instant Payments',
    desc: 'Automated invoicing, secure payment processing, and instant payouts to your account.',
    icon: `<svg viewBox="0 0 32 32" fill="none"><rect x="3" y="8" width="26" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M3 14h26" stroke="currentColor" stroke-width="1.5"/><path d="M8 20h6M22 20h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`
  },
  {
    title: 'Growth Analytics',
    desc: 'Real-time insights on bookings, revenue, and client retention to grow your business smarter.',
    icon: `<svg viewBox="0 0 32 32" fill="none"><path d="M4 24l7-7 5 5 7-9 5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 28h24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`
  },
]

const testimonials = [
  {
    text: "Bubba completely changed how I care for Mochi. I found her groomer through the app and now she has a standing monthly appointment — no more chasing calls.",
    name: "Sarah K.",
    pet: "Owner of Mochi, Shih Tzu",
    initials: "SK",
    color: "oklch(0.75 0.06 71)"
  },
  {
    text: "As a veterinary clinic, Bubba's partner console helped us reduce no-shows by 40%. The automated reminders are a game changer.",
    name: "Dr. James Osei",
    pet: "Pawprint Veterinary Clinic",
    initials: "JO",
    color: "oklch(0.59 0.07 154)"
  },
  {
    text: "My anxious rescue pup has been transformed by the trainer we found on Bubba. The profile matching is genuinely thoughtful.",
    name: "Marcus T.",
    pet: "Owner of Bear, Mixed Breed",
    initials: "MT",
    color: "oklch(0.65 0.11 47)"
  },
]

// Cursor tracking
let cursorX = 0, cursorY = 0
let followerX = 0, followerY = 0
let animFrame = null

function trackCursor(e) {
  cursorX = e.clientX
  cursorY = e.clientY
  if (cursor.value) {
    cursor.value.style.transform = `translate(${cursorX - 6}px, ${cursorY - 6}px)`
  }
}

function animateFollower() {
  followerX += (cursorX - followerX) * 0.12
  followerY += (cursorY - followerY) * 0.12
  if (cursorFollower.value) {
    cursorFollower.value.style.transform = `translate(${followerX - 20}px, ${followerY - 20}px)`
  }
  animFrame = requestAnimationFrame(animateFollower)
}

function onScroll() {
  scrolled.value = window.scrollY > 60
}

function magnetBtn(e) {
  const btn = e.currentTarget
  const rect = btn.getBoundingClientRect()
  const x = e.clientX - rect.left - rect.width / 2
  const y = e.clientY - rect.top - rect.height / 2
  btn.style.transform = `translate(${x * 0.25}px, ${y * 0.25}px)`
}
function resetBtn(e) {
  e.currentTarget.style.transform = ''
}

function tiltCard(e) {
  const card = e.currentTarget
  const rect = card.getBoundingClientRect()
  const x = (e.clientX - rect.left) / rect.width - 0.5
  const y = (e.clientY - rect.top) / rect.height - 0.5
  card.style.transform = `perspective(600px) rotateY(${x * 12}deg) rotateX(${-y * 12}deg) translateZ(10px)`
}
function resetCard(e) {
  e.currentTarget.style.transform = ''
}

let observers = []
function setupObservers() {
  const targets = document.querySelectorAll(
    '.service-card, .feature__left, .feature__right, .partner-card, .testimonial, .feature__stat'
  )
  targets.forEach(el => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view')
          obs.unobserve(entry.target)
        }
      })
    }, { threshold: 0.12 })
    obs.observe(el)
    observers.push(obs)
  })
}

onMounted(() => {
  setTimeout(() => { revealed.value = true }, 100)
  window.addEventListener('mousemove', trackCursor, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })
  animFrame = requestAnimationFrame(animateFollower)
  setTimeout(setupObservers, 400)
})

onUnmounted(() => {
  window.removeEventListener('mousemove', trackCursor)
  window.removeEventListener('scroll', onScroll)
  if (animFrame) cancelAnimationFrame(animFrame)
  observers.forEach(o => o.disconnect())
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* === CSS VARIABLES === */
.landing {
  --cream: oklch(0.95 0.02 83);
  --cream-dark: oklch(0.92 0.02 83);
  --terracotta: oklch(0.6 0.14 41);
  --terracotta-light: oklch(0.67 0.12 39);
  --terracotta-pale: oklch(0.89 0.04 52);
  --forest: oklch(0.44 0.08 155);
  --forest-light: oklch(0.53 0.09 156);
  --forest-pale: oklch(0.91 0.03 160);
  --sand: oklch(0.76 0.1 76);
  --sand-light: oklch(0.85 0.07 78);
  --ink: oklch(0.2 0.01 56);
  --ink-soft: oklch(0.31 0.02 48);
  --muted: oklch(0.59 0.03 59);
  --white: oklch(0.99 0.01 75);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);

  font-family: 'Playfair Display', Georgia, serif;
  background: var(--cream);
  color: var(--ink);
  overflow-x: hidden;
  cursor: none;
}

/* === CURSOR === */
.cursor {
  position: fixed;
  top: 0; left: 0;
  width: 12px; height: 12px;
  background: var(--terracotta);
  border-radius: 50%;
  pointer-events: none;
  z-index: 9999;
  will-change: transform;
}
.cursor-follower {
  position: fixed;
  top: 0; left: 0;
  width: 40px; height: 40px;
  border: 1.5px solid var(--terracotta);
  border-radius: 50%;
  pointer-events: none;
  z-index: 9998;
  will-change: transform;
  opacity: 0.5;
}

/* === NAVIGATION === */
.nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 3rem;
  transition: background 0.4s var(--ease-out), padding 0.3s var(--ease-out), box-shadow 0.3s;
}
.nav--scrolled {
  background: oklch(0.95 0.02 83 / 0.94);
  backdrop-filter: blur(12px);
  padding: 1rem 3rem;
  box-shadow: 0 1px 0 oklch(0.2 0.01 56 / 0.08);
}
.nav__logo {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  text-decoration: none;
}
.nav__logo-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  background: var(--terracotta);
  border-radius: 10px;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 900;
  font-style: normal;
  color: var(--white);
  line-height: 1;
  flex-shrink: 0;
}
.nav__logo-text {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.9rem;
  font-weight: 700;
  font-style: normal;
  color: var(--ink);
  letter-spacing: -0.02em;
  line-height: 1;
}
.nav__links {
  display: flex;
  gap: 2.5rem;
}
.nav__link {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-soft);
  text-decoration: none;
  position: relative;
  transition: color 0.2s;
}
.nav__link::after {
  content: '';
  position: absolute;
  bottom: -2px; left: 0;
  width: 0; height: 1px;
  background: var(--terracotta);
  transition: width 0.3s var(--ease-out);
}
.nav__link:hover { color: var(--ink); }
.nav__link:hover::after { width: 100%; }
.nav__cta {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--white);
  background: var(--ink);
  text-decoration: none;
  padding: 0.7rem 1.6rem;
  border-radius: 100px;
  transition: background 0.2s, transform 0.2s var(--ease-out);
}
.nav__cta:hover {
  background: var(--terracotta);
  transform: translateY(-1px);
}

/* === SECTION LABELS === */
.section-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--terracotta);
  margin-bottom: 1rem;
}
.section-label--light { color: var(--sand); }
.section-label__line {
  display: block;
  width: 2rem; height: 1px;
  background: var(--terracotta);
  flex-shrink: 0;
}
.section-label__line--light { background: var(--sand); }

/* === HERO === */
.hero {
  position: relative;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 7rem 3rem 4rem;
  overflow: hidden;
  background: var(--cream);
}
.hero::before {
  content: '';
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 80% 50%, oklch(0.6 0.14 41 / 0.07) 0%, transparent 70%),
    radial-gradient(ellipse 40% 40% at 20% 85%, oklch(0.44 0.08 155 / 0.05) 0%, transparent 60%);
  pointer-events: none;
}
.hero__bg-text {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(8rem, 20vw, 22rem);
  font-weight: 900;
  letter-spacing: -0.05em;
  color: transparent;
  -webkit-text-stroke: 1px oklch(0.6 0.14 41 / 0.06);
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}
.hero__content {
  position: relative;
  z-index: 2;
  padding-right: 2rem;
}
.hero__eyebrow {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--terracotta);
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.6s var(--ease-out), transform 0.6s var(--ease-out);
}
.hero__eyebrow.visible { opacity: 1; transform: translateY(0); }
.hero__eyebrow-line {
  display: block;
  width: 2rem; height: 1px;
  background: var(--terracotta);
}
.hero__title {
  display: flex;
  flex-direction: column;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(3.5rem, 6vw, 6.5rem);
  font-weight: 900;
  line-height: 0.92;
  letter-spacing: -0.03em;
  color: var(--ink);
  margin-bottom: 2rem;
}
.hero__title em { font-style: italic; color: var(--terracotta); }
.hero__title-line {
  display: block;
  opacity: 0;
  transform: translateY(40px) skewY(2deg);
  transition: opacity 0.8s var(--ease-out) var(--delay, 0s), transform 0.8s var(--ease-out) var(--delay, 0s);
}
.hero__title-line.visible { opacity: 1; transform: translateY(0) skewY(0); }
.hero__title-line--indent { padding-left: 3rem; }
.hero__title-line--accent { color: var(--terracotta); font-style: italic; }

.hero__sub {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.65;
  color: var(--ink-soft);
  margin-bottom: 2.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.7s var(--ease-out) var(--delay, 0s), transform 0.7s var(--ease-out) var(--delay, 0s);
}
.hero__sub.visible { opacity: 1; transform: translateY(0); }

.hero__actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.7s var(--ease-out) var(--delay, 0s), transform 0.7s var(--ease-out) var(--delay, 0s);
}
.hero__actions.visible { opacity: 1; transform: translateY(0); }

/* Hero Mosaic */
.hero__mosaic {
  position: relative;
  z-index: 2;
  height: 68vh;
  min-height: 480px;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  grid-template-rows: 1fr 0.75fr;
  gap: 1rem;
  opacity: 0;
  transform: translateX(40px);
  transition: opacity 1s var(--ease-out) 0.4s, transform 1s var(--ease-out) 0.4s;
}
.hero__mosaic.visible { opacity: 1; transform: translateX(0); }

.mosaic__card {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: transform 0.4s var(--ease-out), box-shadow 0.3s;
  box-shadow: 0 8px 32px oklch(0.2 0.01 56 / 0.1);
  transform-style: preserve-3d;
  will-change: transform;
}
.mosaic__card:hover { box-shadow: 0 16px 48px oklch(0.2 0.01 56 / 0.18); }
.mosaic__card--1 { grid-row: 1; grid-column: 1; }
.mosaic__card--2 { grid-row: 1; grid-column: 2; }
.mosaic__card--3 { grid-row: 2; grid-column: 1 / 3; }

.mosaic__img-wrap { position: absolute; inset: 0; overflow: hidden; }
.mosaic__img {
  position: absolute;
  inset: -5%;
  background-size: cover;
  background-position: center;
  transition: transform 6s ease;
}
.mosaic__card:hover .mosaic__img { transform: scale(1.06); }
.mosaic__img--dog {
  background-color: oklch(0.85 0.07 78);
  background-image: linear-gradient(135deg, oklch(0.6 0.14 41 / 0.25) 0%, oklch(0.76 0.1 76 / 0.15) 100%);
}
.mosaic__img--vet {
  background-color: oklch(0.88 0.03 163);
  background-image: linear-gradient(135deg, oklch(0.44 0.08 155 / 0.2) 0%, oklch(0.53 0.09 156 / 0.1) 100%);
}
.mosaic__img--daycare {
  background-color: oklch(0.88 0.05 80);
  background-image: linear-gradient(135deg, oklch(0.76 0.1 76 / 0.25) 0%, oklch(0.85 0.07 78 / 0.15) 100%);
}
.mosaic__label {
  position: absolute;
  bottom: 1rem; left: 1rem;
  background: oklch(0.95 0.02 83 / 0.92);
  backdrop-filter: blur(8px);
  color: var(--ink);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.3rem 0.75rem;
  border-radius: 100px;
}
.mosaic__stat {
  position: absolute;
  bottom: -0.75rem;
  right: -0.25rem;
  background: var(--forest);
  color: var(--white);
  padding: 1.1rem 1.4rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 8px 24px oklch(0.44 0.08 155 / 0.3);
  z-index: 10;
  transform: rotate(2deg);
}
.mosaic__stat-num {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
}
.mosaic__stat-label {
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  opacity: 0.8;
  margin-top: 0.2rem;
}

/* Scroll indicator */
.hero__scroll {
  position: absolute;
  bottom: 2.5rem;
  left: 3rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
  opacity: 0;
  transition: opacity 0.6s var(--ease-out) var(--delay, 0s);
  z-index: 5;
}
.hero__scroll.visible { opacity: 1; }
.hero__scroll-line {
  width: 1px;
  height: 2.5rem;
  background: linear-gradient(to bottom, var(--terracotta), transparent);
  animation: scrollPulse 2s ease-in-out infinite;
}
@keyframes scrollPulse {
  0%, 100% { opacity: 1; transform: scaleY(1); }
  50% { opacity: 0.3; transform: scaleY(0.5); }
}

/* === BUTTONS === */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-decoration: none;
  padding: 0.85rem 1.75rem;
  border-radius: 100px;
  border: 1.5px solid transparent;
  transition: transform 0.25s var(--ease-out), box-shadow 0.25s, background 0.2s, color 0.2s, border-color 0.2s;
  cursor: none;
  will-change: transform;
}
.btn svg { width: 15px; height: 15px; flex-shrink: 0; }
.btn:hover { transform: translateY(-2px); }
.btn--primary {
  background: var(--ink);
  color: var(--white);
  box-shadow: 0 4px 16px oklch(0.2 0.01 56 / 0.18);
}
.btn--primary:hover {
  background: var(--terracotta);
  box-shadow: 0 8px 24px oklch(0.6 0.14 41 / 0.35);
}
.btn--ghost {
  background: transparent;
  color: var(--ink);
  border-color: oklch(0.2 0.01 56 / 0.2);
}
.btn--ghost:hover {
  border-color: var(--ink);
  background: oklch(0.2 0.01 56 / 0.04);
}
.btn--terracotta {
  background: var(--terracotta);
  color: var(--white);
  box-shadow: 0 4px 16px oklch(0.6 0.14 41 / 0.25);
}
.btn--terracotta:hover {
  background: var(--terracotta-light);
  box-shadow: 0 8px 24px oklch(0.6 0.14 41 / 0.4);
}
.btn--outline {
  background: transparent;
  color: var(--cream);
  border-color: oklch(0.95 0.02 83 / 0.4);
}
.btn--outline:hover {
  border-color: var(--cream);
  background: oklch(0.95 0.02 83 / 0.08);
}
.btn--large { font-size: 1.15rem; padding: 1.1rem 2.4rem; }

/* === MARQUEE === */
.marquee-strip {
  background: var(--ink);
  color: var(--sand-light);
  padding: 0.9rem 0;
  overflow: hidden;
}
.marquee-track {
  display: flex;
  white-space: nowrap;
  animation: marquee 28s linear infinite;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.marquee-track em { color: var(--terracotta-light); font-style: normal; font-weight: 700; }
@keyframes marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-33.333%); }
}

/* === SERVICES === */
.services {
  padding: 8rem 3rem;
  background: var(--cream);
}
.services__header { margin-bottom: 4rem; }
.services__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(3rem, 5vw, 5.5rem);
  font-weight: 800;
  line-height: 1.0;
  letter-spacing: -0.03em;
  color: var(--ink);
}
.services__title em { font-style: italic; color: var(--terracotta); }

.services__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.service-card {
  position: relative;
  padding: 2.5rem 2rem;
  border-radius: 20px;
  background: var(--white);
  border: 1px solid oklch(0.2 0.01 56 / 0.06);
  overflow: hidden;
  opacity: 0;
  transform: translateY(40px);
  transition:
    opacity 0.7s var(--ease-out) calc(var(--i, 0) * 0.08s),
    transform 0.7s var(--ease-out) calc(var(--i, 0) * 0.08s),
    box-shadow 0.3s;
}
.service-card.in-view { opacity: 1; transform: translateY(0); }
.service-card:hover {
  box-shadow: 0 16px 48px oklch(0.2 0.01 56 / 0.1);
  transform: translateY(-4px);
}
.service-card__bg {
  position: absolute; inset: 0;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}
.service-card:hover .service-card__bg { opacity: 1; }
.service-card__number {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--muted);
  margin-bottom: 1.5rem;
}
.service-card__icon {
  width: 3rem; height: 3rem;
  color: var(--terracotta);
  margin-bottom: 1.25rem;
}
.service-card__icon svg { width: 100%; height: 100%; }
.service-card__name {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink);
  margin-bottom: 0.7rem;
}
.service-card__desc {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  line-height: 1.65;
  color: var(--muted);
  margin-bottom: 2rem;
}
.service-card__cta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--terracotta);
  transition: gap 0.2s;
}
.service-card:hover .service-card__cta { gap: 0.85rem; }
.service-card__cta svg { width: 13px; height: 13px; }

/* === FEATURE / ABOUT === */
.feature {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  padding: 8rem 3rem;
  background: var(--cream-dark);
  position: relative;
  overflow: hidden;
}
.feature::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 50% 60% at 0% 50%, oklch(0.44 0.08 155 / 0.05), transparent);
  pointer-events: none;
}
.feature__left {
  position: relative;
  height: 560px;
  opacity: 0;
  transform: translateX(-40px);
  transition: opacity 0.9s var(--ease-out), transform 0.9s var(--ease-out);
}
.feature__left.in-view { opacity: 1; transform: translateX(0); }
.feature__img-stack { position: absolute; inset: 0; }
.feature__img {
  position: absolute;
  border-radius: 20px;
}
.feature__img--back {
  top: 3rem; left: 3rem;
  right: 0; bottom: 0;
  background: linear-gradient(135deg, var(--terracotta-pale) 0%, var(--sand-light) 100%);
  transform: rotate(3deg);
}
.feature__img--front {
  top: 0; left: 0;
  right: 3rem; bottom: 3rem;
  background: linear-gradient(135deg, var(--forest-pale) 0%, oklch(0.95 0.01 160) 100%);
  box-shadow: 0 24px 64px oklch(0.2 0.01 56 / 0.14);
  display: flex;
  align-items: flex-end;
  padding: 1.5rem;
}
.feature__img-tag {
  background: var(--forest);
  color: var(--white);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.35rem 0.85rem;
  border-radius: 100px;
}
.feature__right {
  position: relative;
  z-index: 2;
  opacity: 0;
  transform: translateX(30px);
  transition: opacity 0.9s var(--ease-out) 0.15s, transform 0.9s var(--ease-out) 0.15s;
}
.feature__right.in-view { opacity: 1; transform: translateX(0); }
.feature__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.2rem, 3.5vw, 3.8rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: var(--ink);
  margin-bottom: 1.5rem;
}
.feature__title em { font-style: italic; color: var(--terracotta); }
.feature__body {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.15rem;
  line-height: 1.72;
  color: var(--ink-soft);
  margin-bottom: 2.5rem;
  max-width: 440px;
}
.feature__stats {
  display: flex;
  gap: 2.5rem;
  margin-bottom: 2.5rem;
}
.feature__stat {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.6s var(--ease-out) calc(0.3s + var(--i, 0) * 0.1s),
    transform 0.6s var(--ease-out) calc(0.3s + var(--i, 0) * 0.1s);
}
.feature__stat.in-view { opacity: 1; transform: translateY(0); }
.feature__stat-num {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--terracotta);
  line-height: 1;
}
.feature__stat-label {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: var(--muted);
  text-transform: uppercase;
}

/* === PARTNERS === */
.partners {
  padding: 8rem 3rem;
  background: var(--forest);
  position: relative;
  overflow: hidden;
}
.partners::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 60% 60% at 80% 20%, oklch(0.76 0.1 76 / 0.1), transparent);
  pointer-events: none;
}
.partners__diagonal {
  position: absolute;
  bottom: -1px; right: 0;
  width: 40%;
  height: 120px;
  background: var(--cream);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}
.partners__inner { position: relative; z-index: 2; max-width: 1100px; }
.partners__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.5rem, 4vw, 4rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--white);
  margin-bottom: 1.5rem;
}
.partners__title em { font-style: italic; color: var(--sand); }
.partners__body {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.15rem;
  line-height: 1.7;
  color: oklch(0.95 0.02 83 / 0.6);
  max-width: 480px;
  margin-bottom: 3.5rem;
}
.partner-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  margin-bottom: 3.5rem;
}
.partner-card {
  background: oklch(0.95 0.02 83 / 0.05);
  border: 1px solid oklch(0.95 0.02 83 / 0.1);
  border-radius: 16px;
  padding: 1.75rem 1.5rem;
  opacity: 0;
  transform: translateY(30px);
  transition:
    opacity 0.7s var(--ease-out) calc(var(--i, 0) * 0.1s),
    transform 0.7s var(--ease-out) calc(var(--i, 0) * 0.1s),
    background 0.25s;
}
.partner-card.in-view { opacity: 1; transform: translateY(0); }
.partner-card:hover { background: oklch(0.95 0.02 83 / 0.1); }
.partner-card__icon {
  width: 2.5rem; height: 2.5rem;
  color: var(--sand-light);
  margin-bottom: 1.25rem;
}
.partner-card__icon svg { width: 100%; height: 100%; }
.partner-card__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 0.55rem;
  letter-spacing: -0.01em;
}
.partner-card__desc {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: oklch(0.95 0.02 83 / 0.5);
}

/* === TESTIMONIALS === */
.testimonials {
  padding: 8rem 3rem;
  background: var(--cream);
}
.testimonials__header {
  margin-bottom: 3rem;
}
.testimonials__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2rem, 3.5vw, 3.5rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--ink);
  margin-top: 0.5rem;
}
.testimonials__title em { font-style: italic; color: var(--terracotta); }
.testimonials__track {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.testimonial {
  background: var(--white);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1.5px solid oklch(0.2 0.01 56 / 0.06);
  cursor: none;
  opacity: 0;
  transform: translateY(30px);
  transition:
    opacity 0.7s var(--ease-out),
    transform 0.7s var(--ease-out),
    border-color 0.3s,
    box-shadow 0.3s;
}
.testimonial.in-view { opacity: 1; transform: translateY(0); }
.testimonial--active,
.testimonial:hover {
  border-color: var(--terracotta);
  box-shadow: 0 12px 40px oklch(0.6 0.14 41 / 0.1);
  transform: translateY(-4px);
}
.testimonial__quote {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 4rem;
  line-height: 0.8;
  color: var(--terracotta);
  opacity: 0.25;
  margin-bottom: 0.8rem;
  font-style: italic;
}
.testimonial__text {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.05rem;
  line-height: 1.72;
  color: var(--ink-soft);
  margin-bottom: 2rem;
}
.testimonial__author {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.testimonial__avatar {
  width: 2.5rem; height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--white);
  flex-shrink: 0;
}
.testimonial__name {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
}
.testimonial__pet {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.88rem;
  color: var(--muted);
  margin-top: 0.12rem;
}

/* === CTA === */
.cta-section {
  padding: 8rem 3rem;
  background: var(--terracotta);
  position: relative;
  overflow: hidden;
  text-align: center;
}
.cta-section::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 50% 70% at 50% 50%, oklch(1 0 90 / 0.07), transparent);
  pointer-events: none;
}
.cta-section__bg-word {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(8rem, 18vw, 20rem);
  font-weight: 900;
  letter-spacing: -0.05em;
  color: transparent;
  -webkit-text-stroke: 1px oklch(1 0 90 / 0.1);
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
}
.cta-section__content { position: relative; z-index: 2; }
.cta-section__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.5rem, 4.5vw, 5rem);
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--white);
  margin-bottom: 1.5rem;
}
.cta-section__title em { font-style: italic; color: var(--sand-light); }
.cta-section__sub {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.25rem;
  color: oklch(0.95 0.02 83 / 0.7);
  margin-bottom: 2.5rem;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}
.cta-section__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* === FOOTER === */
.footer {
  background: var(--ink);
  color: var(--cream);
  padding: 4rem 3rem 2rem;
}
.footer__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid oklch(0.95 0.02 83 / 0.08);
}
.footer__brand { max-width: 240px; }
.footer__logo {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.75rem;
}
.footer__logo-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  background: var(--terracotta);
  border-radius: 8px;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 900;
  color: var(--white);
  line-height: 1;
  flex-shrink: 0;
}
.footer__logo-text {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.55rem;
  font-weight: 700;
  color: var(--cream);
  letter-spacing: -0.02em;
  line-height: 1;
}
.footer__tagline {
  font-size: 0.85rem;
  color: oklch(0.95 0.02 83 / 0.4);
  line-height: 1.5;
}
.footer__nav { display: flex; gap: 4rem; }
.footer__nav-col {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.footer__nav-label {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: oklch(0.95 0.02 83 / 0.28);
  margin-bottom: 0.2rem;
}
.footer__nav-col a {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  color: oklch(0.95 0.02 83 / 0.5);
  text-decoration: none;
  transition: color 0.2s;
}
.footer__nav-col a:hover { color: var(--cream); }
.footer__bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.88rem;
  color: oklch(0.95 0.02 83 / 0.28);
}
.footer__legal { display: flex; gap: 1.5rem; }
.footer__legal a {
  color: oklch(0.95 0.02 83 / 0.28);
  text-decoration: none;
  transition: color 0.2s;
}
.footer__legal a:hover { color: oklch(0.95 0.02 83 / 0.65); }

/* === RESPONSIVE === */
@media (max-width: 1100px) {
  .services__grid { grid-template-columns: repeat(2, 1fr); }
  .partner-cards { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 900px) {
  .hero { grid-template-columns: 1fr; padding-top: 8rem; }
  .hero__mosaic { display: none; }
  .feature { grid-template-columns: 1fr; gap: 3rem; }
  .feature__left { height: 340px; }
  .testimonials__track { grid-template-columns: 1fr; }
  .footer__top { flex-direction: column; gap: 2.5rem; }
  .footer__nav { flex-wrap: wrap; gap: 2rem; }
  .nav__links { display: none; }
}
@media (max-width: 640px) {
  .nav { padding: 1.25rem 1.5rem; }
  .hero,
  .services,
  .feature,
  .partners,
  .testimonials,
  .cta-section,
  .footer { padding-left: 1.5rem; padding-right: 1.5rem; }
  .services__grid,
  .partner-cards { grid-template-columns: 1fr; }
  .feature__stats { flex-direction: column; gap: 1.5rem; }
  .cta-section__actions { flex-direction: column; align-items: center; }
}
</style>
